import { createReducer } from '@reduxjs/toolkit'
import { without, forEach, union } from 'lodash-es'
import ActionTypes from '@constants/action_types.js'
import { mergeEntity, mergeEntities } from '@utils/object.js'
import { exists } from '@utils/functions.js'

export const initialState = {}

export const timeline = createReducer(initialState, builder => {
  builder
    .addCase(ActionTypes.RECEIVE_TIMELINE, (state, action) => receiveTimeline(state, action))
    .addCase(ActionTypes.RECEIVE_TIMELINES, (state, action) => receiveTimelines(state, action))
    .addCase(ActionTypes.RECEIVE_MARKER, (state, action) => addMarker(state, action))
    .addCase(ActionTypes.RECEIVE_MARKERS, (state, action) => addMarkers(state, action))
    .addCase(ActionTypes.RECEIVE_TIMESPAN, (state, action) => addTimespan(state, action))
    .addCase(ActionTypes.RECEIVE_TIMESPANS, (state, action) => addTimespans(state, action))
    .addCase(ActionTypes.PHOTO_DELETE_SUCCESS, (state, action) => removePhoto(state, action))
    .addCase(ActionTypes.RECEIVE_ENTITIES, (state, action) => receiveEntities(state, action))
    .addCase(ActionTypes.REMOVE_ENTITY, (state, action) => removeEntity(state, action))
})

export default timeline

function getDefaultAttributes() {
  return {
    id: 0,
    title: '',
    description: '',
    category_ids: [1],
    photo_ids: [],
    marker_ids: [],
    timespan_ids: [],
  }
}
const defaultAttributes = getDefaultAttributes()

function receiveTimeline(state, action) {
  const { payload: timeline } = action
  return mergeEntity(state, timeline, defaultAttributes)
}

function receiveTimelines(state, action) {
  const { payload: timelines } = action
  return mergeEntities(state, timelines, defaultAttributes)
}

function addMarker(state, action) {
  const { payload: marker } = action
  if (exists(marker)) {
    const timelineIdString = marker.timeline_id.toString()
    if (!exists(timelineIdString)) {
      throw new Error('Expected marker to have timeline_id')
    } else {
      state[timelineIdString].marker_ids = union(state[timelineIdString].marker_ids, [marker.id])
    }
  }
  return state
}

function addMarkers(state, action) {
  const { payload: markers } = action
  if (exists(markers)) {
    forEach(markers, marker => {
      const timelineIdString = marker.timeline_id.toString()
      if (!exists(timelineIdString)) {
        throw new Error('Expected marker to have timeline_id')
      } else {
        state[timelineIdString].marker_ids = union(state[timelineIdString].marker_ids, [marker.id])
      }
    })
  }
  return state
}

function addTimespan(state, action) {
  const { payload: timespan } = action
  if (exists(timespan)) {
    const timelineIdString = timespan.timeline_id.toString()
    if (!exists(timelineIdString)) throw new Error('Expected timespan to have timeline_id')
    state[timelineIdString].timespan_ids = union(state[timelineIdString].timespan_ids, [timespan.id])
  }
  return state
}

function addTimespans(state, action) {
  const { payload: timespans } = action
  if (exists(timespans)) {
    forEach(timespans, timespan => {
      const timelineIdString = timespan.timeline_id.toString()
      if (!exists(timelineIdString)) throw new Error('Expected timespan to have timeline_id')
      state[timelineIdString].timespan_ids = union(state[timelineIdString].timespan_ids, [timespan.id])
    })
  }
  return state
}

function removePhoto(state, action) {
  const { payload: photoId } = action
  forEach(state, timeline => {
    timeline.photo_ids.includes(photoId)
    state[timeline.id.toString()].photo_ids = without(timeline.photo_ids, photoId)
  })
  return state
}

function receiveEntities(state, action) {
  const { payload } = action
  if (payload.hasOwnProperty('timeline')) {
    const { timeline } = payload
    state = mergeEntity(state, timeline, defaultAttributes)
  }
  if (payload.hasOwnProperty('timelines')) {
    const { timelines } = payload
    state = mergeEntities(state, timelines, defaultAttributes)
  }
  if (payload.hasOwnProperty('marker')) {
    const { marker } = payload
    const timelineIdString = marker.timeline_id.toString()
    if (!exists(timelineIdString)) {
      throw new Error('Expected marker to have timeline_id')
    } else {
      state[timelineIdString].marker_ids = union(state[timelineIdString].marker_ids, [marker.id])
    }
  }
  if (payload.hasOwnProperty('markers')) {
    const { markers } = payload
    forEach(markers, marker => {
      const timelineIdString = marker.timeline_id.toString()
      state[timelineIdString].marker_ids = union(state[timelineIdString].marker_ids, [marker.id])
    })
  }
  if (payload.hasOwnProperty('timespan')) {
    const { timespan } = payload
    const timelineIdString = timespan.timeline_id.toString()
    if (!exists(timelineIdString)) {
      throw new Error('Expected timespan to have timeline_id')
    } else {
      state[timelineIdString].timespan_ids = union(state[timelineIdString].timespan_ids, [timespan.id])
    }
  }
  if (payload.hasOwnProperty('timespans')) {
    const { timespans } = payload
    forEach(timespans, timespan => {
      const timelineIdString = timespan.timeline_id.toString()
      state[timelineIdString].timespan_ids = union(state[timelineIdString].timespan_ids, [timespan.id])
    })
  }
  return state
}

function removeEntity(state, action) {
  const { payload } = action
  const { entityId, entityType, parentEntityType, parentEntityId } = payload
  if (parentEntityType === 'timeline') {
    if (entityType === 'marker') {
      const timelineIdString = parentEntityId.toString()
      state[timelineIdString].marker_ids = without(state[timelineIdString].marker_ids, entityId)
      return state
    } else if (entityType === 'timespan') {
      const timelineIdString = parentEntityId.toString()
      state[timelineIdString].timespan_ids = without(state[timelineIdString].timespan_ids, entityId)
      return state
    }
  }
  return state
}
